import { useQueryClient } from '@tanstack/react-query';
import { type ErrorComponentProps, DefaultGlobalNotFound } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@worldcoo/ui/components/button';
import { AlertCircle } from 'lucide-react';
import { useServerFn } from '@tanstack/react-start';
import { logErrorFn } from '@/lib/server-functions';
import { useEphemeralStore } from '@/lib/app-store';

export function DefaultCatchBoundary(props: ErrorComponentProps) {
	if ('isNotFound' in props.error) {
		return <DefaultGlobalNotFound />;
	}

	return <CatchBoundary {...props} />;
}

function CatchBoundary(props: ErrorComponentProps) {
	const queryClient = useQueryClient();
	const logError = useServerFn(logErrorFn);
	const { t } = useLingui();
	const clearCheckoutCache = useEphemeralStore((state) => state.clearCheckoutCache);
	const hasLoggedError = useRef(false);

	const isModuleLoadError = props.error.message.includes('error loading dynamically imported module');

	console.error('DefaultCatchBoundary Error:', props.error);

	// Log error to backend
	// If the error was derived from an update that is not retrocompatible (a version skew)
	// this will bust the cache on memory and immediately sync with disk
	useEffect(() => {
		if (hasLoggedError.current) return;
		hasLoggedError.current = true;

		queryClient.clear();
		clearCheckoutCache();

		if (isModuleLoadError) {
			window.location.reload();
			return;
		}

		logError({
			data: {
				route: window.location.pathname,
				name: props.error.name,
				message: props.error.message,
				stack: props.error.stack,
			},
		}).catch((logErr) => {
			console.error('Failed to log error to backend:', logErr);
		});
	}, [queryClient, props.error, logError, clearCheckoutCache, isModuleLoadError]);

	if (isModuleLoadError) return null;

	return (
		<div className="flex flex-col gap-4 text-center">
			<div className="bg-destructive/10 mx-auto flex size-12 items-center justify-center rounded-full">
				<AlertCircle className="text-destructive size-6" />
			</div>

			<div className="flex flex-col gap-2">
				<h2 className="text-xl font-semibold">{t`Something went wrong`}</h2>
				<p className="text-muted-foreground text-sm">
					{t`We're sorry, but something unexpected happened. Please try again or refresh the page.`}
				</p>
			</div>

			{import.meta.env.DEV && props.error instanceof Error && (
				<details className="text-left">
					<summary className="text-muted-foreground cursor-pointer text-sm font-medium">{t`Technical details`}</summary>
					<div className="bg-muted mt-2 rounded-md p-3">
						<p className="font-mono text-xs wrap-break-word">{props.error.message}</p>
						{props.error.stack && (
							<pre className="text-muted-foreground mt-2 max-h-40 overflow-auto font-mono text-xs">
								{props.error.stack}
							</pre>
						)}
					</div>
				</details>
			)}

			<div className="flex flex-col gap-2">
				<Button onClick={() => window.location.reload()} variant="default">
					{t`Try again`}
				</Button>
				<Button onClick={() => (window.location.href = '/')} variant="outline">
					{t`Go to homepage`}
				</Button>
			</div>
		</div>
	);
}
